import React, { useState } from 'react';

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqCategories = [
    {
      category: "General Questions",
      faqs: [
        {
          question: "What services does EmergeNC Diagnostics offer?",
          answer: "We provide comprehensive laboratory testing services including DOT and Non-DOT specimen collection, DNA testing, blood testing, urine drug testing, flu testing, electronic fingerprinting, I-9 background checks, and C/TPA services. We serve individuals, employers, legal professionals, and healthcare providers."
        },
        {
          question: "Do I need an appointment?",
          answer: "While walk-ins are welcome during business hours, we strongly recommend scheduling an appointment to minimize wait times and ensure we can accommodate your specific testing needs. You can book online through our appointment page or call us at (984) 230-9599."
        },
        {
          question: "What are your business hours?",
          answer: "We're open Monday through Friday from 8:00 AM to 5:00 PM, and Saturday from 9:00 AM to 1:00 PM. We are closed on Sundays. Please note that hours may vary during holidays."
        },
        {
          question: "Where are you located?",
          answer: "We're located at 4801 Glenwood Ave Suite 200 PMB 333, Raleigh, NC 27612. Free parking is available on-site with accessible parking spaces near the main entrance."
        }
      ]
    },
    {
      category: "Testing & Results",
      faqs: [
        {
          question: "How long does it take to get my test results?",
          answer: "Turnaround times vary by test type. Rapid tests (like flu testing) provide results in 15-30 minutes. Most standard blood work and drug screens are completed within 24-48 hours. DNA testing typically takes 3-5 business days, while specialized tests may take 5-7 business days. You'll be notified as soon as your results are ready."
        },
        {
          question: "How will I receive my test results?",
          answer: "Results are available through our secure online patient portal, which you can access 24/7. You'll receive an email or text notification when results are ready. For certain tests, results may also be sent directly to your physician or requesting party. Certified paper copies are available upon request."
        },
        {
          question: "Can I access my results online?",
          answer: "Yes! All patients have access to our secure online portal where you can view, download, and print your test results anytime. You'll receive login credentials after your first visit. The portal also maintains your complete testing history for easy reference."
        },
        {
          question: "What if my results are abnormal?",
          answer: "If your results fall outside normal reference ranges or indicate a health concern, we will notify you and your physician immediately. Your healthcare provider will discuss the findings with you and recommend any necessary follow-up testing or treatment."
        }
      ]
    },
    {
      category: "Preparation & Requirements",
      faqs: [
        {
          question: "Do I need to fast before my test?",
          answer: "Fasting requirements depend on the specific test. Common tests requiring fasting (8-12 hours) include lipid panels, glucose tests, and comprehensive metabolic panels. You may drink water during the fasting period. We'll inform you of any preparation requirements when you schedule your appointment."
        },
        {
          question: "What should I bring to my appointment?",
          answer: "Please bring: (1) Valid photo identification (driver's license, passport, or state ID), (2) Insurance card (if applicable), (3) Doctor's order or requisition form (if provided), (4) List of current medications, and (5) Payment method. For drug testing, additional documentation may be required."
        },
        {
          question: "Can I take my medications before testing?",
          answer: "In most cases, yes. However, certain medications can affect test results. Please inform our staff of all medications, supplements, and vitamins you're taking. Your healthcare provider will advise if you need to temporarily discontinue any medications before testing."
        },
        {
          question: "Are there age restrictions for testing?",
          answer: "We provide testing services for all ages. Minors (under 18) must be accompanied by a parent or legal guardian who can provide consent. For certain tests like drug screening, specific age-related regulations may apply."
        }
      ]
    },
    {
      category: "Insurance & Payment",
      faqs: [
        {
          question: "Do you accept insurance?",
          answer: "Yes, we accept most major insurance plans, Medicare, and Medicaid. Coverage varies by plan and test type. We recommend contacting your insurance provider before your visit to verify coverage and understand your out-of-pocket costs. We'll also verify your benefits when you schedule your appointment."
        },
        {
          question: "What if I don't have insurance?",
          answer: "We offer competitive self-pay pricing for patients without insurance. Payment is due at the time of service. We accept cash, credit cards (Visa, MasterCard, Discover, American Express), and debit cards. Payment plans may be available for qualifying patients - please ask our billing department."
        },
        {
          question: "How much will my test cost?",
          answer: "Costs vary depending on the test type and your insurance coverage. We provide cost estimates before testing. For self-pay patients, we offer transparent pricing. Contact our billing department at (984) 230-9599 for specific pricing information."
        },
        {
          question: "Can I get an itemized bill?",
          answer: "Yes, itemized billing statements are available upon request. You can request these through our patient portal, by calling our billing department, or in person. Statements detail all services provided, charges, insurance payments, and your balance."
        }
      ]
    },
    {
      category: "Specialized Services",
      faqs: [
        {
          question: "What is DOT drug testing?",
          answer: "DOT (Department of Transportation) drug testing is federally mandated testing for safety-sensitive employees in transportation industries. It follows strict protocols including a 5-panel drug screen testing for marijuana, cocaine, amphetamines, opioids, and PCP. We are certified to perform DOT collections and maintain compliance with all federal regulations."
        },
        {
          question: "Do you offer legal DNA testing?",
          answer: "Yes, we provide both legal and non-legal DNA testing services. Legal DNA tests follow strict chain-of-custody procedures and are admissible in court for paternity cases, immigration, and other legal matters. Our laboratory is AABB-accredited for relationship testing."
        },
        {
          question: "What is C/TPA service?",
          answer: "C/TPA (Consortium/Third Party Administrator) service provides drug and alcohol testing program management for DOT-regulated employers. We handle random selection, testing coordination, compliance reporting, and maintain all required documentation. This service is ideal for small to medium-sized companies that need DOT compliance support."
        },
        {
          question: "How does electronic fingerprinting work?",
          answer: "Electronic fingerprinting captures your fingerprints digitally using advanced scanning technology. It's faster, more accurate, and cleaner than traditional ink methods. Results are transmitted electronically to the FBI and state agencies for background checks. The process takes about 10-15 minutes and results are typically available within 24-72 hours."
        }
      ]
    },
    {
      category: "Privacy & Compliance",
      faqs: [
        {
          question: "Is my information confidential?",
          answer: "Absolutely. We strictly comply with HIPAA (Health Insurance Portability and Accountability Act) regulations to protect your privacy. Your personal health information is kept confidential and secure. Results are only released to you, your designated physician, or authorized parties with your written consent."
        },
        {
          question: "What certifications does your lab have?",
          answer: "Our laboratory maintains certifications from CLIA (Clinical Laboratory Improvement Amendments), CAP (College of American Pathologists), and is AABB-accredited for DNA relationship testing. We also hold DOT certification for drug and alcohol testing. All certifications are current and regularly audited."
        },
        {
          question: "How do you ensure test accuracy?",
          answer: "We maintain the highest quality standards through: (1) Certified, experienced laboratory technicians, (2) Regular equipment calibration and maintenance, (3) Participation in proficiency testing programs, (4) Strict quality control procedures, (5) Chain-of-custody protocols for forensic testing, and (6) Compliance with all federal and state regulations."
        },
        {
          question: "Can I request my medical records?",
          answer: "Yes, you have the right to access your medical records. You can request copies through our patient portal, by submitting a written request, or by calling our office. There may be a nominal fee for printed copies. We typically fulfill requests within 5-7 business days."
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex: number, faqIndex: number) => {
    const globalIndex = categoryIndex * 100 + faqIndex;
    setOpenIndex(openIndex === globalIndex ? null : globalIndex);
  };

  return (
    <div className="bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex text-sm" aria-label="Breadcrumb">
            <a href="/" className="text-[#4AB8D8] hover:text-[#F8A356]">Home</a>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-600">FAQ</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#4AB8D8] to-[#3A9AB8] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Find answers to common questions about our laboratory testing services, procedures, and policies
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search Box */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Search FAQs by keyword..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4AB8D8] focus:border-transparent"
              aria-label="Search FAQs"
            />
            <button className="bg-[#4AB8D8] text-white px-6 py-3 rounded-lg hover:bg-[#3A9AB8] transition-colors font-semibold">
              Search
            </button>
          </div>
        </div>

        {/* FAQ Categories */}
        {faqCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#4AB8D8]">
              {category.category}
            </h2>
            <div className="space-y-3">
              {category.faqs.map((faq, faqIndex) => {
                const globalIndex = categoryIndex * 100 + faqIndex;
                const isOpen = openIndex === globalIndex;
                
                return (
                  <div key={faqIndex} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <button
                      onClick={() => toggleFAQ(categoryIndex, faqIndex)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      aria-expanded={isOpen}
                    >
                      <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                      <svg
                        className={`w-6 h-6 text-[#4AB8D8] flex-shrink-0 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#4AB8D8] to-[#3A9AB8] rounded-lg p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Didn't Find Your Answer?</h2>
          <p className="text-xl mb-6 text-white/90">Our team is ready to help with any questions you may have</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-white text-[#4AB8D8] px-8 py-3 rounded-lg hover:bg-[#F8A356] hover:text-white transition-colors font-semibold">
              Contact Us
            </a>
            <a href="tel:9842309599" className="bg-[#3A9AB8] text-white px-8 py-3 rounded-lg hover:bg-[#F8A356] transition-colors font-semibold">
              Call (984) 230-9599
            </a>
            <a href="/appointment" className="bg-[#3A9AB8] text-white px-8 py-3 rounded-lg hover:bg-[#F8A356] transition-colors font-semibold">
              Book Appointment
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 bg-[#4AB8D8]/10 rounded-lg p-6">
          <h3 className="font-semibold text-lg mb-4 text-gray-900">Quick Links</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <a href="/services" className="text-[#4AB8D8] hover:text-[#F8A356] font-medium flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              View All Services
            </a>
            <a href="/test-catalog" className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Browse Test Catalog
            </a>
            <a href="/patient-resources" className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Patient Resources
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
